import { NewsPost, Example } from '../types/content';
import matter from 'gray-matter';
import { Buffer } from 'buffer';

// Polyfill Buffer for browser environment
if (typeof window !== 'undefined' && !(window as any).Buffer) {
  (window as any).Buffer = Buffer;
  (globalThis as any).Buffer = Buffer;
}

interface StreamingOptions {
  onProgress?: (loaded: number, total: number) => void;
  onItem?: (item: NewsPost | Example) => void;
  batchSize?: number;
}

/**
 * Load content with streaming/progressive updates
 */
export async function loadNewsPostsStreaming(
  filePaths: string[], 
  options: StreamingOptions = {}
): Promise<NewsPost[]> {
  const { onProgress, onItem, batchSize = 3 } = options;
  const posts: NewsPost[] = [];
  const total = filePaths.length;
  
  // Process files in batches for better performance
  for (let i = 0; i < filePaths.length; i += batchSize) {
    const batch = filePaths.slice(i, i + batchSize);
    
    // Load batch in parallel
    const batchPromises = batch.map(async (filePath) => {
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          console.error(`Failed to fetch ${filePath}:`, response.status);
          return null;
        }
        
        const markdown = await response.text();
        const { data: frontmatter, content } = matter(markdown);
        
        const slug = filePath.split('/').pop()?.replace('.md', '') || '';
        
        const post: NewsPost = {
          slug,
          title: frontmatter.title || '',
          dek: frontmatter.dek || '',
          source_name: frontmatter.source_name || '',
          source_url: frontmatter.source_url || '',
          published_at: frontmatter.published_at || new Date().toISOString(),
          topics: frontmatter.topics || [],
          curator_note: frontmatter.curator_note || '',
          featured: frontmatter.featured || false,
          content
        };
        
        return post;
      } catch (error) {
        console.error(`Error loading ${filePath}:`, error);
        return null;
      }
    });
    
    // Wait for batch to complete
    const batchResults = await Promise.all(batchPromises);
    
    // Add successful loads to results
    for (const post of batchResults) {
      if (post) {
        posts.push(post);
        onItem?.(post);
      }
    }
    
    // Report progress
    const loaded = Math.min(i + batchSize, total);
    onProgress?.(loaded, total);
    
    // Small delay to prevent overwhelming the browser
    if (i + batchSize < filePaths.length) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }
  
  // Sort by date
  return posts.sort((a, b) => 
    new Date(b.published_at).getTime() - new Date(a.published_at).getTime()
  );
}

/**
 * Load content with immediate partial results for better perceived performance
 */
export async function loadNewsPostsWithEarlyReturn(filePaths: string[]): Promise<{
  initialPosts: NewsPost[];
  remainingPromise: Promise<NewsPost[]>;
}> {
  // Load first few files immediately for quick display
  const earlyPaths = filePaths.slice(0, 3);
  const remainingPaths = filePaths.slice(3);
  
  // Load initial batch
  const initialPromises = earlyPaths.map(async (filePath) => {
    try {
      const response = await fetch(filePath);
      if (!response.ok) return null;
      
      const markdown = await response.text();
      const { data: frontmatter, content } = matter(markdown);
      const slug = filePath.split('/').pop()?.replace('.md', '') || '';
      
      return {
        slug,
        title: frontmatter.title || '',
        dek: frontmatter.dek || '',
        source_name: frontmatter.source_name || '',
        source_url: frontmatter.source_url || '',
        published_at: frontmatter.published_at || new Date().toISOString(),
        topics: frontmatter.topics || [],
        curator_note: frontmatter.curator_note || '',
        featured: frontmatter.featured || false,
        content
      } as NewsPost;
    } catch {
      return null;
    }
  });
  
  const initialResults = await Promise.all(initialPromises);
  const initialPosts = initialResults.filter((post): post is NewsPost => post !== null);
  
  // Load remaining files
  const remainingPromise = loadNewsPostsStreaming(remainingPaths).then(remainingPosts => {
    const allPosts = [...initialPosts, ...remainingPosts];
    return allPosts.sort((a, b) => 
      new Date(b.published_at).getTime() - new Date(a.published_at).getTime()
    );
  });
  
  return {
    initialPosts,
    remainingPromise
  };
}